import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CustomerAuthContext = createContext();

const API_URL   = process.env.REACT_APP_API_URL || 'https://freshhillsmilk.in/api';
const TOKEN_KEY = 'fh_customer_token';
const USER_KEY  = 'fh_customer';

// axios instance — auto-attaches Bearer token
const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export const useCustomerAuth = () => {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error('useCustomerAuth must be inside CustomerAuthProvider');
  return ctx;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  });
  const [authLoading, setAuthLoading] = useState(true);

  // Verify stored token on app start
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAuthLoading(false); return; }
    api.get('/auth/verify-token')
      .then(() => setAuthLoading(false))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setCustomer(null);
        setAuthLoading(false);
      });
  }, []);

  // ── Send OTP ──────────────────────────────────────────────────────────────
  const sendOtp = useCallback(async (phone, channel = 'whatsapp') => {
    const res = await api.post('/auth/send-otp', { phone, channel });
    return res.data;
  }, []);

  // ── Verify OTP (called once — does NOT send name) ─────────────────────────
  // Returns { success, token, customer: { id, name, phone, ... } }
  // If customer.name is empty → frontend should call saveName() next
  const verifyOtp = useCallback(async (phone, otp) => {
    const res = await api.post('/auth/verify-otp', { phone, otp });
    if (res.data.success) {
      // Save token immediately so saveName() can use it
      localStorage.setItem(TOKEN_KEY, res.data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data.customer));
      setCustomer(res.data.customer);
    }
    return res.data;
  }, []);

  // ── Save name for new users (uses JWT, no OTP re-check) ───────────────────
  const saveName = useCallback(async (name) => {
    const res = await api.post('/auth/save-name', { name });
    if (res.data.success) {
      const updated = { ...res.data.customer };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      setCustomer(updated);
    }
    return res.data;
  }, []);

  // ── Fetch profile ─────────────────────────────────────────────────────────
  const fetchProfile = useCallback(async () => {
    const res = await api.get('/auth/profile');
    if (res.data.success) {
      localStorage.setItem(USER_KEY, JSON.stringify(res.data.customer));
      setCustomer(res.data.customer);
    }
    return res.data;
  }, []);

  // ── Update profile ────────────────────────────────────────────────────────
  const updateProfile = useCallback(async (fields) => {
    const res = await api.put('/auth/profile', fields);
    if (res.data.success) {
      const updated = { ...customer, ...fields };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      setCustomer(updated);
    }
    return res.data;
  }, [customer]);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setCustomer(null);
  }, []);

  return (
    <CustomerAuthContext.Provider value={{
      customer,
      authLoading,
      isLoggedIn: !!customer,
      sendOtp,
      verifyOtp,
      saveName,
      fetchProfile,
      updateProfile,
      logout,
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};